import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { quotesAPI, jobsAPI, aiAPI, emailAPI } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Edit, Send, FileText, Trash2, Sparkles, Loader2, Download, Link, Copy, Eye, X, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const RATE_STOP = new Set(['supply', 'install', 'installation', 'and', 'the', 'a', 'of', 'to', 'for', 'with', 'per', 'each', 'ea', 'no', 'nr', 'inc', 'incl', 'including', 'as', 'at', 'on', 'in', 'new', 'existing', 'labour', 'only', 'provide']);
const normalizeRate = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/)
  .filter((w) => w && !RATE_STOP.has(w))
  .map((w) => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w))
  .join(' ').trim();

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  accepted: 'status-accepted',
  rejected: 'status-rejected',
  expired: 'status-cancelled',
};

export const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [learnedRates, setLearnedRates] = useState([]);
  const [targetMargin, setTargetMargin] = useState(20);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [formData, setFormData] = useState({
    job_id: '',
    client_name: '',
    client_email: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    valid_until: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, jobsRes] = await Promise.all([
        quotesAPI.getAll(),
        jobsAPI.getAll()
      ]);
      setQuotes(quotesRes.data);
      setJobs(jobsRes.data);
      try {
        const [ratesRes, marginRes] = await Promise.all([
          api.get('/learned-rates'),
          api.get('/settings/margin'),
        ]);
        setLearnedRates(ratesRes.data || []);
        setTargetMargin(marginRes.data?.target_margin_pct ?? 20);
      } catch (_) { /* hints are optional */ }
      // Deep-link: open a specific quote for editing (e.g. right after creating a draft from a PO)
      const openId = searchParams.get('openQuote');
      if (openId) {
        const q = quotesRes.data.find((x) => x.id === openId);
        if (q) { handleEditQuote(q); }
        searchParams.delete('openQuote');
        setSearchParams(searchParams, { replace: true });
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setFormData({
      ...formData,
      job_id: jobId,
      client_name: job?.client_name || '',
      client_email: job?.client_email || '',
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const findRate = (desc) => {
    const key = normalizeRate(desc);
    if (!key || key.length < 3 || learnedRates.length === 0) return null;
    const exact = learnedRates.find((r) => r.normalized_key === key);
    if (exact) return exact;
    const kt = new Set(key.split(' '));
    let best = null, bestScore = 0;
    for (const r of learnedRates) {
      const rt = new Set((r.normalized_key || '').split(' '));
      if (!rt.size) continue;
      const inter = [...kt].filter((x) => rt.has(x)).length;
      const uni = new Set([...kt, ...rt]).size;
      const score = inter / Math.max(uni, 1);
      if (inter >= 1 && score > bestScore) { best = r; bestScore = score; }
    }
    return bestScore >= 0.34 ? best : null;
  };

  const applyRate = (index, price) => {
    const newItems = [...formData.items];
    newItems[index].unit_price = price;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleAIGenerate = async () => {
    const job = jobs.find(j => j.id === formData.job_id);
    if (!job) {
      toast.error('Please select a job first');
      return;
    }

    setAiLoading(true);
    try {
      const response = await aiAPI.generateQuote({
        job_description: `${job.title}\n${job.description || ''}\nAddress: ${job.address}`,
        job_type: job.title
      });
      
      if (response.data.suggestion) {
        // Try to parse the AI response
        try {
          const parsed = JSON.parse(response.data.suggestion);
          if (Array.isArray(parsed)) {
            const newItems = parsed.map(item => ({
              description: item.description,
              quantity: item.quantity || 1,
              unit_price: item.unit_price || 0
            }));
            setFormData({ ...formData, items: newItems });
            toast.success('AI suggestions loaded!');
          }
        } catch {
          toast.info('AI suggestion generated - please review and adjust manually');
        }
      }
    } catch (error) {
      toast.error('Failed to generate AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuote) {
        // Check if this is a sent quote being edited - will create revision
        const wasSent = ['sent', 'accepted', 'rejected'].includes(editingQuote.status);
        const response = await quotesAPI.update(editingQuote.id, formData);
        
        if (wasSent && response.data?.revision_number > (editingQuote.revision_number || 0)) {
          toast.success(`Quote revised - now REV ${response.data.revision_number}`);
        } else {
          toast.success('Quote updated');
        }
      } else {
        await quotesAPI.create(formData);
        toast.success('Quote created');
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await quotesAPI.update(quoteId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await quotesAPI.downloadPdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote-${quoteNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleCopyClientLink = async (clientToken) => {
    const link = `${window.location.origin}/client/quote/${clientToken}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success('Client link copied to clipboard');
      } else {
        // Fallback for older browsers or when clipboard API is not available
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Client link copied to clipboard');
      }
    } catch (err) {
      // If all else fails, show the link in a prompt
      toast.info('Copy this link:', { description: link, duration: 10000 });
      console.error('Failed to copy:', err);
    }
  };

  const handleSendQuote = async (quote) => {
    if (!quote.client_email) {
      toast.error('No client email address');
      return;
    }
    
    const clientLink = `${window.location.origin}/client/quote/${quote.client_token}`;
    const htmlContent = `
      <h2>Quote from My Project Agent</h2>
      <p>Dear ${quote.client_name},</p>
      <p>Please find your quote <strong>${quote.quote_number}</strong> attached.</p>
      <p><strong>Total: $${quote.total?.toFixed(2)}</strong></p>
      <p><a href="${clientLink}" style="background-color: #F97316; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px;">View & Approve Quote</a></p>
      <p>Thank you for your business!</p>
      <p>My Project Agent Team</p>
    `;
    
    try {
      await emailAPI.send({
        recipient_email: quote.client_email,
        subject: `Quote ${quote.quote_number} from My Project Agent`,
        html_content: htmlContent,
        related_type: 'quote',
        related_id: quote.id
      });
      await quotesAPI.update(quote.id, { status: 'sent' });
      toast.success(`Quote sent to ${quote.client_email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingQuote(null);
    setFormData({
      job_id: '',
      client_name: '',
      client_email: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      valid_until: '',
    });
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setFormData({
      job_id: quote.job_id || '',
      client_name: quote.client_name || '',
      client_email: quote.client_email || '',
      items: quote.items?.length > 0 
        ? quote.items 
        : (quote.sections?.flatMap(s => s.items) || [{ description: '', quantity: 1, unit_price: 0 }]),
      notes: quote.notes || '',
      valid_until: quote.valid_until || '',
    });
    setDialogOpen(true);
  };

  const handleViewPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await quotesAPI.downloadPdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Quote ${quoteNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const filteredQuotes = quotes.filter(q =>
    q.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Quotes</h1>
          <p className="text-muted-foreground mt-1">Create and manage job quotes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="font-semibold rounded-lg" data-testid="add-quote-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuote ? 'Edit Quote' : 'Create New Quote'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Select Job *</Label>
                  <Select value={formData.job_id} onValueChange={handleJobChange}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="quote-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="bg-input border-border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="quote-client-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="bg-input border-border rounded-lg"
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAIGenerate}
                      disabled={!formData.job_id || aiLoading}
                      className="rounded-lg"
                    >
                      {aiLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      AI Suggest
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-lg">
                      <Plus className="h-4 w-4 mr-1" /> Add Item
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 border border-border rounded-lg p-3">
                  {formData.items.map((item, index) => {
                    const hint = findRate(item.description);
                    const cost = hint?.cost;
                    const price = Number(item.unit_price) || 0;
                    const marginPct = (cost && cost > 0 && price > 0) ? ((price - cost) / price) * 100 : null;
                    const belowTarget = marginPct != null && marginPct < targetMargin;
                    return (
                    <div key={index}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="col-span-6 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="col-span-3 bg-input border-border rounded-lg text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="col-span-1"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    {hint && (
                      <button
                        type="button"
                        onClick={() => applyRate(index, hint.avg_price)}
                        className="mt-1 ml-1 inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline"
                        data-testid={`rate-hint-${index}`}
                        title={`Learned from ${hint.times_seen} PO(s) · ${hint.confidence} confidence`}
                      >
                        <TrendingUp className="h-3 w-3" />
                        Typical: ${Number(hint.avg_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="text-muted-foreground">({hint.times_seen}× · {hint.confidence})</span>
                        <span className="text-primary font-medium">— tap to use</span>
                      </button>
                    )}
                    {marginPct != null && (
                      <div
                        className={`mt-1 ml-1 inline-flex items-center gap-1.5 text-[11px] ${belowTarget ? 'text-red-500 font-semibold' : 'text-emerald-500'}`}
                        data-testid={`margin-guard-${index}`}
                      >
                        {belowTarget && <AlertTriangle className="h-3 w-3" />}
                        Margin {marginPct.toFixed(0)}% (cost ${Number(cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        {belowTarget && <span>· below your {targetMargin}% target</span>}
                      </div>
                    )}
                    </div>
                    );
                  })}
                  <div className="flex justify-end pt-2 border-t border-border">
                    <p className="font-mono font-bold">
                      Subtotal: ${calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-lg"
                  rows={3}
                  placeholder="Terms, conditions, or additional notes..."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                <Button type="submit" className="font-semibold rounded-lg" data-testid="quote-submit">
                  {editingQuote ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search quotes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-lg"
          data-testid="quote-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4" />
              <p>No quotes found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Quote #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotes.map((quote) => {
                  const displayNumber = quote.revision_number > 0 
                    ? `${quote.quote_number} REV ${quote.revision_number}` 
                    : quote.quote_number;
                  
                  return (
                  <TableRow key={quote.id} className="border-border" data-testid={`quote-row-${quote.id}`}>
                    <TableCell className="font-mono text-sm">
                      {displayNumber}
                      {quote.revision_number > 0 && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Revised
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{quote.client_name}</TableCell>
                    <TableCell className="font-mono">${quote.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      {quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={quote.status} onValueChange={(v) => handleStatusChange(quote.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[quote.status]} rounded-lg text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(quote.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="Edit Quote" onClick={() => handleEditQuote(quote)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(quote.id, displayNumber)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send Quote" onClick={() => handleSendQuote(quote)}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(quote.id, displayNumber)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Copy Client Link" onClick={() => handleCopyClientLink(quote.client_token)}>
                          <Link className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-800">{pdfTitle}</h3>
              <div className="flex items-center gap-2">
                <a 
                  href={pdfUrl} 
                  download={`${pdfTitle}.pdf`}
                  className="inline-flex items-center px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </a>
                <Button variant="ghost" size="icon" onClick={() => { setPdfViewerOpen(false); window.URL.revokeObjectURL(pdfUrl); }} className="text-gray-600 hover:text-gray-800">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-gray-200 overflow-hidden">
              <object 
                data={pdfUrl} 
                type="application/pdf"
                className="w-full h-full"
              >
                <div className="flex flex-col items-center justify-center h-full text-gray-600">
                  <p className="mb-4">Unable to display PDF in browser.</p>
                  <a 
                    href={pdfUrl} 
                    download={`${pdfTitle}.pdf`}
                    className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                  >
                    Download PDF
                  </a>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesPage;
